import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { convertNumberToWords } from './utility';

const BLUE = [70, 99, 255];
const NAVY = [30, 45, 70];
const PALE = [241, 245, 255];
const BORDER = [221, 228, 242];
const text = value => String(value ?? '').replace(/[\u00a0\u202f]/g, ' ');
const number = value => text(Number(value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const date = value => value ? String(value).slice(0, 10).split('-').reverse().join('/') : '-';

export default function exportSalesDetailsPdf({ report, company, filters, clientLabel, rows, allRows, pageNumber, pageSize, kind = 'sales' }) {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const width = doc.internal.pageSize.getWidth();
    const height = doc.internal.pageSize.getHeight();
    const margin = 14;
    const contentWidth = width - margin * 2;
    const totalQuantity = allRows.reduce((sum, row) => sum + Number(row.quantity || 0), 0);
    const totalAmount = allRows.reduce((sum, row) => sum + Number(row.lineTotal || 0), 0);
    const pageQuantity = rows.reduce((sum, row) => sum + Number(row.quantity || 0), 0);
    const pageAmount = rows.reduce((sum, row) => sum + Number(row.lineTotal || 0), 0);
    const companyName = company?.name || company?.businessName || 'MATJARPOS';
    const from = report.period?.from || filters.dateDebut;
    const to = report.period?.to || filters.dateFin;
    const purchases = kind === 'purchases';
    const title = purchases ? 'Détails des achats' : 'Détails des ventes';
    const header = () => {
        doc.setFillColor(...BLUE);
        doc.rect(0, 0, width, 27, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold'); doc.setFontSize(19);
        doc.text(title, margin, 13);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
        doc.text(`Quantités ${purchases ? 'achetées' : 'vendues'} par article · Du ${date(from)} au ${date(to)}`, margin, 21);
        doc.text(doc.splitTextToSize(text(companyName), 90), width - margin, 12, { align: 'right', maxWidth: 90 });
        doc.setTextColor(...NAVY);
    };
    header();
    doc.setFontSize(10);
    const context = [`${purchases ? 'Fournisseur' : 'Client'} : ${clientLabel || report.supplier?.name || report.client?.name || 'Tous'}`, `Article / code-barres : ${filters.query || 'Tous'}`];
    let y = 35;
    context.forEach(line => {
        const wrapped = doc.splitTextToSize(text(line), contentWidth);
        doc.text(wrapped, margin, y); y += wrapped.length * 4.5 + 2;
    });
    const metrics = [
        [purchases ? 'ARTICLES ACHETÉS' : 'ARTICLES VENDUS', String(allRows.length)],
        ['QUANTITÉ TOTALE', number(totalQuantity)],
        [purchases ? 'TOTAL ACHATS' : 'TOTAL VENTES', `${number(totalAmount)} DH`],
        ['PAGE AFFICHÉE', `${pageNumber + 1}/${Math.max(1, Math.ceil(allRows.length / pageSize))}`],
    ];
    const boxWidth = (contentWidth - 18) / 4;
    metrics.forEach(([label, value], index) => {
        const x = margin + index * (boxWidth + 6);
        doc.setFillColor(...PALE); doc.setDrawColor(...BORDER);
        doc.roundedRect(x, y, boxWidth, 20, 2, 2, 'FD');
        doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(103, 115, 135);
        doc.text(label, x + 4, y + 6);
        doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...NAVY);
        doc.text(value, x + 4, y + 15);
    });
    y += 29;
    doc.setFontSize(12); doc.text(purchases ? 'Total acheté par article' : 'Total vendu par article', margin, y);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
    const first = allRows.length ? pageNumber * pageSize + 1 : 0;
    doc.text(`Affichage ${first} à ${Math.min((pageNumber + 1) * pageSize, allRows.length)} sur ${allRows.length} résultats. Totaux de toute la période.`, margin, y + 6);
    const tableStyle = {
        theme: 'grid', margin: { top: 35, right: margin, bottom: 18, left: margin },
        styles: { font: 'helvetica', fontSize: 9, cellPadding: 3, textColor: NAVY, lineColor: BORDER, lineWidth: 0.15, overflow: 'linebreak' },
        headStyles: { fillColor: BLUE, textColor: [255, 255, 255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 255] },
        rowPageBreak: 'avoid',
        willDrawPage: () => { if (doc.internal.getCurrentPageInfo().pageNumber > 1) header(); },
    };
    autoTable(doc, {
        ...tableStyle, startY: y + 10,
        head: [['Désignation / code-barres', purchases ? 'Quantité achetée' : 'Quantité vendue', 'Total']],
        body: rows.length ? rows.map(row => [text(`${row.designation || '-'}\n${row.barcode || 'Sans code-barres'}`), text(`${row.quantity ?? 0}\n${row.unit || ''}`), number(row.lineTotal)]) : [[{ content: purchases ? 'Aucun article acheté sur cette période.' : 'Aucun article vendu sur cette période.', colSpan: 3 }]],
        columnStyles: { 0: { cellWidth: contentWidth * .61 }, 1: { cellWidth: contentWidth * .23, halign: 'center' }, 2: { halign: 'right' } },
        foot: [['Total page', number(pageQuantity), number(pageAmount)], ['Total résultats', number(totalQuantity), number(totalAmount)]],
        footStyles: { fillColor: PALE, textColor: BLUE, fontStyle: 'bold' }, showFoot: 'lastPage',
    });
    if (report.summary) {
        const summary = report.summary;
        let summaryY = doc.lastAutoTable.finalY + 10;
        if (summaryY + 70 > height - 18) { doc.addPage(); header(); summaryY = 35; }
        autoTable(doc, {
            ...tableStyle, startY: summaryY, head: [['Résumé', 'Montant (DH)']],
            body: [
                ['Montant Total Consommé', number(summary.totalConsome)],
                [`Solde Avant le ${date(from)}`, number(summary.soldeAvant)],
                ['Avance', number(summary.avance)],
                ['Règlements normaux (hors avances)', number(summary.reglements)],
                ['Avoir', number(summary.avoir)],
                ['Net à payer', number(summary.netApaye)],
            ], columnStyles: { 1: { halign: 'right' } },
        });
        let wordsY = doc.lastAutoTable.finalY + 8;
        doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
        const words = doc.splitTextToSize(text(`Arrêté le présent document à la somme de : ${Number(summary.netApaye) < 0 ? 'Moins ' : ''}${convertNumberToWords(Math.abs(Number(summary.netApaye || 0)))} Dirhams`), contentWidth);
        if (wordsY + words.length * 4.5 > height - 18) { doc.addPage(); header(); wordsY = 35; }
        doc.setTextColor(...NAVY); doc.text(words, margin, wordsY);
    }
    const pages = doc.getNumberOfPages();
    for (let page = 1; page <= pages; page++) {
        doc.setPage(page); doc.setDrawColor(...BORDER); doc.line(margin, height - 13, width - margin, height - 13);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(103, 115, 135);
        doc.text(text(companyName).slice(0, 70), margin, height - 8);
        doc.text(`Page ${page} / ${pages}`, width / 2, height - 8, { align: 'center' });
        doc.text(`Généré le ${text(report.generationDate || new Date().toLocaleDateString('fr-FR'))}`, width - margin, height - 8, { align: 'right' });
    }
    doc.save(`details-${purchases ? 'achats' : 'ventes'}-${from || 'periode'}-page-${pageNumber + 1}.pdf`);
}
